import firebase, { storage } from "../../config/firebase";
import { fetchAllLabels } from "./labelActions";
import { toast } from "react-toastify";
import algoliasearch from "algoliasearch";
import { createNullCache } from "@algolia/cache-common";

const client = algoliasearch("GPYJEAVUBP", "b1f999bc5b264defe930762391d240c6", {
  responsesCache: createNullCache(),
});

const db = firebase.firestore().collection("items");

export const fetchItems =
  ({ restaurantID, categoryId }) =>
  async (dispatch) => {
    try {
      const itemsSnapshot = await db
        .where("restaurantID", "==", restaurantID)
        .where("categoryId", "==", categoryId)
        .where("isVisible", "==", true)
        .orderBy("createdAt", "desc")
        .get();

      const items = await Promise.all(
        itemsSnapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const addOnIds = Array.isArray(data.addOns) ? data.addOns : [];

          const addOnsData = await Promise.all(
            addOnIds.map((addOn) => {
              const addOnId = addOn?.id;
              if (!addOnId) return null;
              return firebase
                .firestore()
                .collection("addOns")
                .doc(addOnId)
                .get()
                .then((d) => (d.exists ? { id: d.id, ...d.data() } : null));
            })
          );

          return {
            id: docSnap.id,
            ...data,
            addOns: addOnsData.filter(Boolean),
          };
        })
      );

      dispatch({ type: "FETCH_ITEM_SUCCESS", payload: items });
      return items;
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Unable to fetch items");
      throw error;
    }
  };

const activeItemListeners = {};

export const fetchCachedItems =
  ({ restaurantID, categoryId }) =>
  async (dispatch, getState) => {
    const cacheKey = `${restaurantID}_${categoryId}`;
    const state = getState();
    const cachedItems = state.item.itemCache?.[cacheKey];

    if (cachedItems) return () => {};

    try {
      const cacheKey = `${restaurantID}_${categoryId}`;

      const itemsRef = firebase
        .firestore()
        .collection("items")
        .where("restaurantID", "==", restaurantID)
        .where("categoryId", "==", categoryId)
        .where("isVisible", "==", true)
        .orderBy("createdAt", "desc");

      // Setup listener only once per category
      if (!activeItemListeners[cacheKey]) {
        const unsubscribe = itemsRef.onSnapshot(async (itemsSnapshot) => {
          const state = getState(); // refreshed for labels cache
          const labels =
            state.item.labelsCache?.[restaurantID] ??
            (await dispatch(fetchAllLabels(restaurantID)));

          const items = await Promise.all(
            itemsSnapshot.docs.map(async (docSnap) => {
              const data = docSnap.data();
              const addOnIds = Array.isArray(data.addOns) ? data.addOns : [];

              const addOnsData = await Promise.all(
                addOnIds.map((addOn) => {
                  const addOnId = addOn?.id;
                  if (!addOnId) return null;
                  return firebase
                    .firestore()
                    .collection("addOns")
                    .doc(addOnId)
                    .get()
                    .then((d) => (d.exists ? { id: d.id, ...d.data() } : null));
                })
              );

              const itemLabelIds = Array.isArray(data.labels)
                ? data.labels.map((label) =>
                    typeof label === "object" ? label.id : label
                  )
                : [];

              const itemLabels = labels?.filter((label) =>
                itemLabelIds.includes(label.id)
              );

              return {
                id: docSnap.id,
                ...data,
                addOns: addOnsData.filter(Boolean),
                fullLabels: itemLabels || [],
              };
            })
          );

          dispatch({
            type: "CACHE_CATEGORY_ITEMS",
            payload: { key: cacheKey, items },
          });
        });

        activeItemListeners[cacheKey] = unsubscribe;
        return unsubscribe;
      }

      return activeItemListeners[cacheKey];
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Unable to fetch items");
      throw error;
    }
  };

export const fetchItemsByRestaurantPaginated =
  ({ restaurantID, query = "", page = 0, hitsPerPage = 10 }) =>
  async () => {
    try {
      const index = client.initIndex("items");
      const response = await index.search(query, {
        filters: `restaurantID:${restaurantID} AND isVisible:true`,
        hitsPerPage,
        page,
      });

      return {
        items: response.hits,
        totalPages: response.nbPages,
        currentPage: response.page,
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const fetchOffersByRestaurantPaginated =
  ({ restaurantID, query = "", page = 0, hitsPerPage = 10 }) =>
  async () => {
    try {
      const index = client.initIndex("offers");
      const response = await index.search(query, {
        filters: `restaurantID:${restaurantID}`,
        hitsPerPage,
        page,
      });

      return {
        offers: response.hits,
        totalPages: response.nbPages,
        currentPage: response.page,
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const fetchAllItems = (restaurantID) => (dispatch) => {
  return new Promise(async (resolve, reject) => {
    try {
      const itemsSnapshot = await db
        .where("restaurantID", "==", restaurantID)
        .get();

      const items = itemsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      dispatch({
        type: "FETCH_ALL_ITEMS_SUCCESS",
        payload: items,
      });
      resolve(items);
    } catch (error) {
      console.error(error.message);
      reject(error);
    }
  });
};

export const addItem =
  ({ categoryId, payload }) =>
  (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const itemRef = db.doc();
        const itemId = itemRef.id;

        const imageUrls = [];

        for (const imageFile of payload.images) {
          const storageRef = storage.ref(`items/${itemId}/${imageFile.name}`);
          await storageRef.put(imageFile);
          const imageUrl = await storageRef.getDownloadURL();
          imageUrls.push(imageUrl);
        }

        const itemData = {
          ...payload,
          categoryId,
          images: imageUrls,
          isVisible: true,
          createdAt: firebase.firestore.Timestamp.now(),
          updatedAt: firebase.firestore.Timestamp.now(),
        };

        await itemRef.set(itemData);

        dispatch({
          type: "ADD_ITEM_SUCCESS",
          payload: { ...itemData, id: itemId },
        });

        resolve(itemData);
      } catch (error) {
        console.error(error.message);
        toast.error(error.message || "Unable to add Item");
        reject(error);
      }
    });
  };

export const updateItem =
  ({ itemId, categoryId, payload, removedImages }) =>
  (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const itemRef = db.doc(itemId);

        for (const imageUrl of removedImages) {
          try {
            const imageRef = storage.refFromURL(imageUrl);
            await imageRef.delete();
          } catch (err) {
            console.error("Error occured while deleting image", err);
          }
        }

        let updatedImages = payload.images.filter(
          (image) => !removedImages.includes(image)
        );

        for (const imageFile of payload.images) {
          if (imageFile instanceof File) {
            const storageRef = storage.ref(`items/${itemId}/${imageFile.name}`);
            await storageRef.put(imageFile);
            const imageUrl = await storageRef.getDownloadURL();
            updatedImages.push(imageUrl);
          }
        }

        updatedImages = updatedImages.filter(
          (image) => typeof image === "string"
        );

        const updatedItemData = {
          ...payload,
          categoryId,
          images: updatedImages,
          updatedAt: firebase.firestore.Timestamp.now(),
        };

        await itemRef.update(updatedItemData);

        toast.success("Item Updated Successfully");
        dispatch({
          type: "UPDATE_ITEM_SUCCESS",
          payload: { ...updatedItemData, id: itemId },
        });

        resolve(updatedItemData);
      } catch (error) {
        console.error(error.message);
        toast.error(error.message || "Unable to update Item");
        reject(error);
      }
    });
  };

export const deleteItem = (itemId, images) => (dispatch) => {
  return new Promise(async (resolve, reject) => {
    try {
      await db.doc(itemId).delete();

      for (const imageUrl of images) {
        try {
          const imageRef = storage.refFromURL(imageUrl);
          await imageRef.delete();
        } catch (err) {
          console.error("Error occured while deleting image", err);
        }
      }

      dispatch({
        type: "DELETE_ITEM_SUCCESS",
        payload: itemId,
      });

      resolve(itemId);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message || "Unable to delete Item");
      reject(error);
    }
  });
};

export const toggleItemVisibility = (itemId, isVisible) => (dispatch) => {
  return new Promise(async (resolve, reject) => {
    try {
      await db.doc(itemId).update({ isVisible });

      toast.success("Item visibility updated successfully!");
      dispatch({
        type: "UPDATE_ITEM_SUCCESS",
        payload: { id: itemId, isVisible },
      });

      resolve({ id: itemId, isVisible });
    } catch (error) {
      console.error(error.message);
      toast.error(error.message || "Unable to update Item visibility");
      reject(error);
    }
  });
};

export const duplicateItem =
  ({ categoryId, itemData }) =>
  async (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const newItemRef = db.doc();
        const newItemId = newItemRef.id;

        const newImageUrls = [];
        for (const imageUrl of itemData.images) {
          const imageName = imageUrl.split("/").pop();
          const imageStorageRef = storage.ref(
            `items/${newItemId}/${imageName}`
          );

          const imageBlob = await fetch(imageUrl).then((res) => res.blob());

          await imageStorageRef.put(imageBlob);
          const newImageUrl = await imageStorageRef.getDownloadURL();
          newImageUrls.push(newImageUrl);
        }

        const newItemData = {
          ...itemData,
          categoryId,
          images: newImageUrls,
          createdAt: firebase.firestore.Timestamp.now(),
          updatedAt: firebase.firestore.Timestamp.now(),
        };

        await newItemRef.set(newItemData);

        dispatch({
          type: "ADD_ITEM_SUCCESS",
          payload: { ...newItemData, id: newItemId },
        });

        resolve(newItemData);
      } catch (error) {
        console.error(error.message);
        toast.error(error.message || "Unable to duplicate Item");
        reject(error);
      }
    });
  };
